import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Question } from '@/components/onboarding/enhance/enhanceStep.types';
import { QuestionCard } from '@/components/onboarding/QuestionCard';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useEnhanceResume } from '@/hooks/useEnhanceResume';

interface EnhanceStepProps {
  onBack: () => void;
  onNext: (resumeMd: string) => void;
  onSkip: () => void;
  resumeText: string;
}

export const EnhanceStep = ({
  onBack,
  onNext,
  onSkip,
  resumeText,
}: EnhanceStepProps) => {
  const enhance = useEnhanceResume();
  const { mutate: triggerEnhance } = enhance;
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [resumeMd, setResumeMd] = useState<string | null>(null);
  const [phase, setPhase] = useState<
    'loading' | 'questions' | 'generating' | 'preview'
  >('loading');

  useEffect(() => {
    triggerEnhance(
      { resumeText },
      {
        onError: () => {
          setPhase('questions');
        },
        onSuccess: (result) => {
          if (result.questions) {
            setQuestions(result.questions);

            const initialAnswers: Record<string, string> = {};

            for (const question of result.questions) {
              initialAnswers[question.id] = '';
            }

            setAnswers(initialAnswers);
            setPhase('questions');
          }
        },
      },
    );
  }, [triggerEnhance, resumeText]);

  const generateResume = (errorPhase: 'questions' | 'preview') => {
    setPhase('generating');

    const formattedAnswers = questions.map(({ id, question }) => ({
      answer: answers[id] || '',
      question,
    }));

    enhance.mutate(
      { answers: formattedAnswers, resumeText },
      {
        onError: () => {
          setPhase(errorPhase);
        },
        onSuccess: (result) => {
          if (result.resume_md) {
            setResumeMd(result.resume_md);
            setPhase('preview');
          }
        },
      },
    );
  };

  const handleSubmitAnswers = () => generateResume('questions');

  const handleRegenerate = () => generateResume('preview');

  if (phase === 'loading' || phase === 'generating') {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <Spinner label="" size="lg" />
        <p className="text-xs lg:text-base text-foreground-muted">
          {phase === 'loading'
            ? 'Analyzing your resume...'
            : 'Generating enhanced resume...'}
        </p>
      </div>
    );
  }

  if (phase === 'preview' && resumeMd) {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-lg lg:text-xl font-semibold">
            Enhanced resume ready
          </h2>
          <p className="mt-1 text-xs lg:text-base text-foreground-muted">
            Review the draft. Accept it, or regenerate for another pass.
          </p>
        </div>

        <textarea
          className="max-h-96 min-h-64 w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm/relaxed lg:text-base/relaxed text-foreground transition-colors placeholder:text-foreground-muted/40 focus:border-accent/40 focus:outline-none"
          onChange={(event) => setResumeMd(event.target.value)}
          value={resumeMd}
        />

        {enhance.isError && (
          <p className="text-center text-sm lg:text-base text-score-low">
            Error: {enhance.error.message}
          </p>
        )}

        <div className="flex justify-between">
          <Button onClick={onBack} size="md" type="button" variant="ghost">
            <ChevronLeft className="size-3.5" /> Back
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={handleRegenerate}
              size="md"
              type="button"
              variant="ghost"
            >
              Regenerate
            </Button>
            <Button
              onClick={() => onNext(resumeMd)}
              size="md"
              type="button"
              variant="primary"
            >
              Accept <ChevronRight className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const answeredCount = Object.values(answers).filter(Boolean).length;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg lg:text-xl font-semibold">
          Let Claude sharpen your resume
        </h2>
        <p className="mt-1 text-sm lg:text-base text-foreground-muted">
          Optional. Answer a few questions and we'll write a tailored base
          resume.
        </p>
      </div>

      {enhance.isError && (
        <p className="text-center text-sm lg:text-base text-score-low">
          Failed to load questions: {enhance.error.message}
        </p>
      )}

      {questions.length > 0 && (
        <div className="space-y-3">
          {questions.map((question, index) => (
            <QuestionCard
              answer={answers[question.id] || ''}
              index={index}
              key={question.id}
              onAnswer={(value) =>
                setAnswers((prev) => ({ ...prev, [question.id]: value }))
              }
              question={question}
            />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button onClick={onBack} size="md" type="button" variant="ghost">
          <ChevronLeft className="size-3.5" /> Back
        </Button>
        <div className="flex items-center gap-3">
          <Button onClick={onSkip} size="md" type="button" variant="ghost">
            Skip
          </Button>
          {questions.length > 0 && (
            <span className="font-mono text-2xs lg:text-xs text-subtle">
              {answeredCount}/{questions.length} answered
            </span>
          )}
          <Button
            disabled={questions.length === 0}
            onClick={handleSubmitAnswers}
            size="md"
            type="button"
            variant="primary"
          >
            Generate resume <ChevronRight className="size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
